import { useRef, useState, useEffect } from 'react';
import MediaCard from './MediaCard';
import type { Movie, TvShow, WatchProgress } from '../../types';

interface MediaRowProps {
  title: string;
  items: (Movie | TvShow | WatchProgress)[];
  type?: 'movie' | 'tv';
  showProgress?: boolean;
  loading?: boolean;
  index?: number;
}

export default function MediaRow({ title, items, type, showProgress = false, loading = false, index }: MediaRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkArrows = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => {
    checkArrows();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkArrows);
      return () => ref.removeEventListener('scroll', checkArrows);
    }
  }, [items]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // Defensive: items may be undefined briefly when a caller hands us an
  // in-flight prop (e.g. an API result that hasn't resolved yet). Treat
  // undefined the same as empty — render nothing, don't crash on .length.
  const safeItems = items ?? [];
  if (!loading && safeItems.length === 0) return null;

  return (
    <section
      className="relative py-4 group/row animate-fade-rise"
      style={{ animationDelay: `${Math.min(index ?? 0, 6) * 0.08}s` }}
    >
      {/* Section Header — clean accent-bar style */}
      <div className="mb-3 px-4 sm:px-6 lg:px-12 flex items-end justify-between group/head">
        <h2 className="flex items-center gap-3 text-lg sm:text-xl font-semibold">
          <span className="h-5 w-[3px] rounded-full bg-[var(--color-primary)]" aria-hidden />
          <span className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            {title}
          </span>
          {loading && (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
        </h2>

        {/* Explore All — hover-revealed (dead button preserved; no real destination prop to wire) */}
        {!loading && safeItems.length > 6 && (
          <button className="text-xs sm:text-sm text-gray-400 hover:text-white transition-all duration-300 opacity-0 group-hover/head:opacity-100 translate-x-1 group-hover/head:translate-x-0">
            Explore all →
          </button>
        )}
      </div>

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow - Enhanced design */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-0 bottom-0 z-20 w-16 bg-gradient-to-r from-[#151210] via-[#151210]/80 to-transparent flex items-center justify-start pl-4 opacity-0 group-hover/row:opacity-100 transition-opacity"
            aria-label="Scroll left"
          >
            <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 hover:bg-black/80 hover:border-white/40 hover:scale-110 flex items-center justify-center transition-all shadow-xl">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </button>
        )}

        {/* Items */}
        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-5 lg:gap-6 overflow-x-auto overflow-y-hidden hide-scrollbar px-4 sm:px-6 lg:px-12 py-6"
          style={{ overscrollBehaviorX: 'contain', overscrollBehaviorY: 'auto' }}
        >
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-40 sm:w-44 lg:w-48">
                  <div className="aspect-[2/3] rounded-lg shimmer" />
                </div>
              ))
            : safeItems.map((item, index) => {
                const itemId = 'mediaId' in item ? item.mediaId : item.id;
                // Get media_type from trending items, or use passed type prop
                const itemType = 'mediaType' in item
                  ? item.mediaType
                  : ('media_type' in item ? (item as { media_type: string }).media_type as 'movie' | 'tv' : type);
                return (
                  <MediaCard
                    key={`${itemId}-${itemType}-${index}`}
                    media={item}
                    type={itemType}
                    showProgress={showProgress}
                  />
                );
              })}
        </div>

        {/* Right Arrow - Enhanced design */}
        {showRightArrow && !loading && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-0 bottom-0 z-20 w-16 bg-gradient-to-l from-[#151210] via-[#151210]/80 to-transparent flex items-center justify-end pr-4 opacity-0 group-hover/row:opacity-100 transition-opacity"
            aria-label="Scroll right"
          >
            <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 hover:bg-black/80 hover:border-white/40 hover:scale-110 flex items-center justify-center transition-all shadow-xl">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        )}
      </div>
    </section>
  );
}
